'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { SpaceCanvasHost } from '@toolproof-core/visualization/rendering/space-canvas';

import { DEFAULT_SPACE_SLUG, getRegisteredEpisodeAuditionOwners, getRoutableSpaceNavigationOptions, getSpaceAdapterDescriptorBySlug } from '@/adapters/_@shared/spaceAdapterRegistry';
import NativeAdapterDriver from '@/drivers/nativeAdapterDriver/NativeAdapterDriver';
import { parseCosmosRoute, resolveCosmosDestination, ROUTE_ORIGIN_PARAM } from '@/_pure/cosmosRoute';
import { resolveEpisodeAuditionOwner, withEpisodeAuditionRoutes } from '@/_pure/episodeSupport';
import { loadScenePlanAuditions } from '@/loadScenePlanAuditions';
import { XrAgentChat } from '@/agentChat/XrAgentChat';
import { CosmosDiagnosticsProvider } from '@/CosmosDiagnosticsProvider';
import { useAgentChat } from '@/agentChat/AgentChatProvider';
import { selectedAgentChanges } from '@/_pure/agentChanges';
import { selectedPlannedFiles } from '@/_pure/agentPlannedFiles';

/** Keeps one canvas (and any XR session) alive while routes swap the space rendered into it. */
export default function CosmosRouteHost() {
    // `?xrEmulator=1` lets testers without a headset enter XR on non-localhost origins such as Forge.
    const [xrEmulator, setXrEmulator] = useState(false);
    useEffect(() => setXrEmulator(new URLSearchParams(window.location.search).get('xrEmulator') === '1'), []);
    return <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
        <CosmosDiagnosticsProvider>
            <SpaceCanvasHost sceneOverlay={<XrAgentChat />} xrEmulator={xrEmulator}>
                <Suspense fallback={null}>
                    <CosmosRouteContent />
                </Suspense>
            </SpaceCanvasHost>
        </CosmosDiagnosticsProvider>
    </div>;
}

function CosmosRouteContent() {
    const agent = useAgentChat();
    const pathname = usePathname();
    const arrivalOrigin = useSearchParams().get(ROUTE_ORIGIN_PARAM) ?? undefined;
    const route = parseCosmosRoute(pathname);
    const auditionOwners = process.env.NODE_ENV === 'development' ? getRegisteredEpisodeAuditionOwners() : [];
    const requestedHandle = route.kind === 'episode' || route.kind === 'space' ? route.episodeHandle : undefined;
    const auditionOwner = requestedHandle ? resolveEpisodeAuditionOwner(requestedHandle, auditionOwners) : undefined;
    const auditionHandle = auditionOwner && (route.kind !== 'space' || route.spaceSlug === auditionOwner.spaceSlug)
        ? requestedHandle : undefined;
    const auditionSupport = auditionHandle ? auditionOwner?.auditions : undefined;
    const [auditionLoad, setAuditionLoad] = useState<{ handle: string; error?: string }>();
    useEffect(() => {
        if (!auditionHandle || !auditionSupport || auditionSupport.resolveEpisode(auditionHandle)) return;
        const controller = new AbortController();
        void loadScenePlanAuditions(auditionSupport, controller.signal).then(() => {
            if (!auditionSupport.resolveEpisode(auditionHandle)) throw new Error('Stored audition is unavailable.');
            if (!controller.signal.aborted) setAuditionLoad({ handle: auditionHandle });
        }).catch((failure: unknown) => {
            if (controller.signal.aborted) return;
            console.error('[CosmosRouteHost] Audition load failed:', failure);
            setAuditionLoad({ handle: auditionHandle, error: failure instanceof Error ? failure.message : String(failure) });
        });
        return () => controller.abort();
    }, [auditionHandle, auditionSupport]);
    const spaces = getRoutableSpaceNavigationOptions().map((option) => {
        const descriptor = getSpaceAdapterDescriptorBySlug(option.spaceSlug);
        return { spaceSlug: option.spaceSlug, episodeHandles: descriptor && 'episodePresentation' in descriptor
            ? [...descriptor.episodePresentation.episodes,
                ...(process.env.NODE_ENV === 'development' ? descriptor.episodePresentation.developerEpisodes ?? [] : [])]
                .map((episode) => episode.handle) : [] };
    });
    if (auditionHandle && auditionSupport && !auditionSupport.resolveEpisode(auditionHandle)) {
        if (auditionLoad?.handle === auditionHandle && auditionLoad.error) return <div role="alert">
            {auditionLoad.error}
            {auditionLoad.error === 'Unauthorized' ? <> — <a href={`/api/auth/signin?callbackUrl=${encodeURIComponent(pathname)}`}>
                Sign in to preview auditions
            </a></> : null}
        </div>;
        return <div role="status">Loading episode plan audition...</div>;
    }
    const destination = resolveCosmosDestination(route, process.env.NODE_ENV === 'development'
        ? withEpisodeAuditionRoutes(spaces, auditionOwners) : spaces, DEFAULT_SPACE_SLUG);
    if (destination.error !== undefined) return <div role="alert">{destination.error}</div>;
    const { spaceSlug, episodeHandle } = destination;
    return <NativeAdapterDriver key={spaceSlug} spaceSlug={spaceSlug} episodeHandle={episodeHandle}
        arrivalOrigin={arrivalOrigin} agentChanges={selectedAgentChanges(agent.state)} agentPlannedFiles={selectedPlannedFiles(agent.state)} />;
}
